/**
 * lib/sync/syncContext.ts — CONTEXTO OPERATIVO VIGENTE + GENERACIÓN (0B.10 P5).
 *
 * Antes, `isActive` tenía por defecto `() => true`: cualquier corrida lanzada fuera del
 * worker (el hook de QA, `useProducts`, una llamada directa) se consideraba SIEMPRE
 * vigente, así que una sincronización lenta de T01 podía PROMOVER su catálogo después
 * de que el usuario ya había cambiado a T02 — publicando datos del alcance equivocado.
 * `disposed` sólo protegía a las corridas nacidas dentro de `startDataSyncWorker`.
 *
 * Aquí el contexto vigente es GLOBAL y versionado por una GENERACIÓN monotónica. Un
 * guard capturado al arrancar una corrida deja de ser válido en cuanto:
 *   • cambia el usuario, la empresa o la tienda; o
 *   • se cierra sesión (contexto nulo); o
 *   • se instala cualquier contexto nuevo (la generación avanza).
 *
 * Comparar sólo los ids no basta: si el usuario va de T01 a T02 y VUELVE a T01, los
 * ids coinciden otra vez pero la corrida vieja ya no debe promover. La generación es
 * lo que distingue "mismo alcance" de "misma corrida".
 */
import type { SyncContext } from './syncWorker'

interface ActiveContext {
  userId: string
  businessId: string | null
  storeId: string
  generation: number
}

let current: ActiveContext | null = null
let generation = 0

/** Instala el contexto operativo vigente y devuelve su generación. */
export function setActiveSyncContext(ctx: SyncContext): number {
  generation += 1
  current = { ...ctx, generation }
  return generation
}

/** Logout / desmontaje: no hay contexto vigente; toda corrida en vuelo queda inválida. */
export function clearActiveSyncContext(): void {
  generation += 1
  current = null
}

/** Sólo para pruebas/diagnóstico. */
export function getActiveSyncContext(): Readonly<ActiveContext> | null {
  return current
}

/**
 * Guard REAL para una corrida: captura la generación vigente al crearse y exige que
 * siga siendo la misma Y que el alcance (usuario/empresa/tienda) no haya cambiado.
 *
 * ESTRICTO por diseño (0B.11 P1): si NO hay contexto instalado —típicamente tras un
 * logout— el guard es FALSO. Antes devolvía un guard permisivo "para las pruebas del
 * motor", y eso abría justo el agujero que se quería cerrar: tras cerrar sesión,
 * `clearActiveSyncContext` dejaba `current = null`, el guard pasaba a permisivo y una
 * corrida del alcance anterior seguía sincronizando (se comprobó que llegaba a
 * consultar la red). Sin contexto operativo no se sincroniza nada.
 */
export function makeContextGuard(ctx: SyncContext): () => boolean {
  const startedAt = current?.generation ?? null
  if (startedAt === null) return () => false
  return () =>
    current !== null &&
    current.generation === startedAt &&
    current.userId === ctx.userId &&
    current.businessId === ctx.businessId &&
    current.storeId === ctx.storeId
}
